import type {
  ChartInstrument,
  ChartInstrumentVisualization,
} from 'lib-analytics-types';
import debounce from 'lodash/debounce';
import { PropsWithoutRef, useCallback, useMemo, useState } from 'react';
import { useList, useUpdateEffect } from 'react-use';
import { LegendProps } from 'recharts';

import {
  ChartInstrumentLegend,
  ChartInstrumentLegendProps,
} from './components/legend/chartInstrumentLegend';

type UseChartInstrumentLegendOptions = {
  instrument: ChartInstrument;
  visualization: ChartInstrumentVisualization;
};

type UseChartInstrumentLegendResult = {
  legend: string[];
  legendProps: PropsWithoutRef<LegendProps>;
  hoveredLegend: string | null;
};

/**
 * A hook that returns the props and state for the legend component of a chart.
 *
 * The hook is used to abstract the logic and UI of the chart legend as you
 * would usually do with custom components, because the Recharts library does
 * not allow wrapping their chart components into custom components.
 */
export const useChartInstrumentLegend = ({
  instrument,
  visualization,
}: UseChartInstrumentLegendOptions): UseChartInstrumentLegendResult => {
  const {
    data: { series, options },
  } = instrument;
  const isHorizontal = options?.legend?.layout === 'horizontal';

  const [legend, legendSetters] = useList<string>(
    series.map(({ label }) => label),
  );

  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);

  // Updates the active legend items when the instrument data series changes.
  useUpdateEffect(() => {
    handleInitLegend();
  }, [series]);

  const handleInitLegend = useCallback(() => {
    legendSetters.set(series.map(({ label }) => label));
  }, [series, legendSetters]);

  const handleMouseEnterLegendItem = useCallback(
    (label: string) => {
      if (legend.includes(label)) {
        setHoveredLegend(label);
      }
    },
    [legend],
  );

  /**
   * Debounce prevents the flickering of the hover state updates when the user
   * is moving their cursor around the legend
   */
  const debouncedHandleMouseEnterLegendItem = useMemo(
    () => debounce(handleMouseEnterLegendItem, 300),
    [handleMouseEnterLegendItem],
  );

  const handleMouseLeaveLegendItem = useCallback(() => {
    debouncedHandleMouseEnterLegendItem.cancel();
    setHoveredLegend(null);
  }, [debouncedHandleMouseEnterLegendItem]);

  const handleToggleLegend = useCallback(
    (label: string): void => {
      if (legend.length === series.length) {
        // When all legend items are visible, clicking on a legend item will isolate it.
        legendSetters.filter((item) => item === label);
      } else if (legend.length === 1 && legend.includes(label)) {
        // When only the current legend item is visible, clicking it will show all legend items.
        handleInitLegend();
      } else if (legend.includes(label)) {
        legendSetters.removeAt(legend.indexOf(label));
      } else {
        legendSetters.push(label);
      }
    },
    [series.length, handleInitLegend, legend, legendSetters],
  );

  // Hoverable legend items don't work well when there are a lot of series items, because there
  // are simply too many lines or bars in the chart to be able to distinguish them.
  const mouseEventsProps: Partial<ChartInstrumentLegendProps> = useMemo(
    () =>
      series.length < 10
        ? {
            onMouseEnterLegendItem: debouncedHandleMouseEnterLegendItem,
            onMouseLeaveLegendItem: handleMouseLeaveLegendItem,
            hoveredLegend,
          }
        : {},
    [
      debouncedHandleMouseEnterLegendItem,
      handleMouseLeaveLegendItem,
      hoveredLegend,
      series.length,
    ],
  );

  const legendProps: Partial<LegendProps> = useMemo(
    () => ({
      layout: options?.legend?.layout,
      align: isHorizontal ? 'center' : 'right',
      verticalAlign: isHorizontal ? 'bottom' : 'middle',
      wrapperStyle: {
        overflow: 'auto',
        ...(isHorizontal
          ? {
              bottom: 0,
            }
          : {
              right: 0,
              width: 208,
              maxHeight: '100%',
            }),
      },
      // Using the render function form instead of directly passing an element prevents the
      // `ChartInstrumentLegend` component from re-rendering unnecessarily due to Recharts props
      // changing, because Recharts automatically passes props to the `content` element.
      content: () => (
        <ChartInstrumentLegend
          visualization={visualization}
          instrument={instrument}
          legend={legend}
          onToggleLegendItem={handleToggleLegend}
          {...mouseEventsProps}
        />
      ),
    }),
    [
      handleToggleLegend,
      instrument,
      isHorizontal,
      legend,
      mouseEventsProps,
      options?.legend?.layout,
      visualization,
    ],
  );

  return {
    legend,
    legendProps,
    hoveredLegend,
  };
};
